/**
 * firmsConnector.ts
 *
 * Fetches NASA FIRMS wildfire hotspot CSV via Lambda proxy and converts
 * to Float32Array with stride 5: [lon, lat, altMeters, value, rotationRad]
 *
 * rotationRad is always 0.0 for wildfires (no heading).
 * stride-5 matches scOverlayPass instance buffer layout.
 *
 * CORS: Routes through Lambda proxy to keep MAP_KEY server-side.
 * Refresh interval: 5 minutes (FIRMS near-real-time data cadence).
 *
 * CSV columns used: latitude, longitude, frp (Fire Radiative Power in MW)
 */

import Papa from 'papaparse';

/** Number of floats per marker: lon, lat, alt, value, rotationRad */
export const FIRMS_STRIDE = 5;

/** Altitude offset so markers appear above terrain (~5km) */
const FIRMS_ALT_OFFSET = 5000;

/** FRP normalization divisor (typical max FRP ~1000 MW) */
const FRP_MAX = 1000.0;

/** Lambda proxy endpoint for FIRMS CSV */
const FIRMS_PROXY_URL = (process.env.REACT_APP_API_URL || 'https://f423zq3mxf.execute-api.us-east-1.amazonaws.com/prod') + '/api/geo/firms';

interface FIRMSRow {
  latitude?: string;
  longitude?: string;
  frp?: string;
  confidence?: string;
  [key: string]: string | undefined;
}

/**
 * Fetch FIRMS wildfire hotspot data via Lambda proxy and convert to Float32Array.
 *
 * Layout per entry (stride=5):
 *   [0] lon           — WGS84 longitude in degrees
 *   [1] lat           — WGS84 latitude in degrees
 *   [2] altMeters     — fixed 5000m above terrain
 *   [3] value         — frp / 1000, clamped to [0, 1]
 *   [4] rotationRad   — always 0.0 (wildfires have no heading)
 *
 * Rows missing latitude or longitude are filtered out.
 * Rows with missing/invalid frp are included with value=0.
 *
 * @returns Float32Array of length validRows * 5, or null on error.
 */
export async function fetchFIRMSHotspots(): Promise<Float32Array | null> {
  // AbortSignal.timeout may not be available in all environments (e.g. older jsdom)
  const signal = typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(15_000)
    : undefined;

  let response: Response;
  try {
    response = await fetch(FIRMS_PROXY_URL, { signal });
  } catch {
    return null;
  }

  if (!response.ok) return null;

  const csvText = await response.text();
  const result = Papa.parse<FIRMSRow>(csvText, { header: true, skipEmptyLines: true });

  const rows = result.data.filter(
    (r) => r.latitude && r.latitude.trim() !== '' && r.longitude && r.longitude.trim() !== ''
  );

  const buf = new Float32Array(rows.length * FIRMS_STRIDE);
  let i = 0;

  for (const r of rows) {
    const lon = parseFloat(r.longitude!);
    const lat = parseFloat(r.latitude!);
    const frp = parseFloat(r.frp ?? '0');
    // NaN frp → treat as 0 (keep row, don't skip)
    const frpSafe = Number.isNaN(frp) ? 0 : frp;
    const value = Math.max(0, Math.min(1, frpSafe / FRP_MAX));

    buf[i++] = lon;
    buf[i++] = lat;
    buf[i++] = FIRMS_ALT_OFFSET;
    buf[i++] = value;
    buf[i++] = 0.0;  // rotationRad — wildfires have no heading
  }

  return buf;
}
